import axios from 'axios';

class API {

	constructor(context) {
		this.context = context;
	}

	store() {
		return (this.context && this.context.store) || (global.app && global.app.$store);
	}

	static baseUrl() {
		let base = process.env.VUE_APP_API_BASE_URL || 'https://gravityfox.com/';
		let prefix = process.env.VUE_APP_API_URL_PREFIX || 'api/';

		return base + prefix;
	}

	instance() {
		// Singleton api client
		if (this.apiClient) return this.apiClient;

		// Create axios client
		this.apiClient = axios.create({
			baseURL: API.baseUrl(),
			withCredentials: true
		});


		// Use request interceptors
		this.apiClient.interceptors.request.use(config => {
			let token = null;

			// Get current token in cookies
			if (process.server) {
				token = this.context.req.cookies.token;
			} else if (this.store()) {
				token = this.store().state.auth.token;
			}

			config.headers['Authorization'] = token ? `Bearer ${ token }` : null;

			return config;
		});

		// Use response interceptor
		this.apiClient.interceptors.response.use(
			this.handleSuccess.bind(this),
			this.handleError.bind(this)
		);

		return this.apiClient;
	}

	handleSuccess(response) {
		if (response.headers.authorization && this.store()) {
			this.store().dispatch('setToken', response.headers.authorization.substring(7));
		}

		return Promise.resolve(response);
	}

	async handleError(error) {
		// TODO - This fails to handle login redirection on server load. It means if someone goes ot the site and is logged out, they'll see an auth page flash for a second
		if (!process.client) return;

		if (typeof error.response === 'undefined') {
			if (error.__CANCEL__) return Promise.reject(error);
			if (this.store()) {
				this.store().dispatch('addToast', { message: 'A network error has occurred. Please try again later.', type: 'bad-bg' });
			}
		}

		switch (error.response.status) {
			case 401:
				// Unauthenticated
				if (this.store()) {
					// TODO - Re-login should story last loginid, let you login in a modal, if new loginId matches, keep where you left off
					await this.store().dispatch('logout');
					this.store().dispatch('addToast', { message: 'You have been logged out', type: 'bad-bg', unique: true });
					if (window && window.location.pathname !== '/login') {
						window.location.href = '/login';
					}
				}

				break;
			case 419:
				// CSRF Failure. Re-fetch token & re-issue request
				try {
					await this.instance().get('/sanctum/csrf-cookie');
				} catch (e) {
					console.log('failed to get csrf cookie', e.message);
				}
				if (error.config) {
					return this.instance().request(error.config);
				}
				break;
			case 403:
				if (error.response.data && error.response.data.type === 'invalid-account' && this.store()) {
					this.store().dispatch('invalidAccount');
					// global.app.$store.dispatch('addToast', { message: API.getErrorMessage(error), type: 'bad-bg' });
					this.store().dispatch('addToast', { message: 'You do not have access to this account', type: 'bad-bg' });
					if (global.app) {
						global.app.$router.push('/dashboard');
					}
				}
				break;
			default:
				// global.app.$toastr('error', API.getErrorMessage(error));
				break;
		}

		return Promise.reject(error);
	}

	getErrorMessage(error) {
		if (typeof error.response === 'undefined') {
			return error.message;
		}

		const response = error.response;
		const data = response.data;

		if (response.status >= 500) {
			return 'Sorry! An internal error has occurred';
		}

		// Check for form validation errors
		if (data.hasOwnProperty('errors')) {
			let error = data.errors[ Object.keys(data.errors)[ 0 ] ];
			if (typeof error === 'string' ) {
				return error;
			}

			return error[ 0 ];
		}

		if (typeof data === 'object') {
			return data[ Object.keys(data)[ 0 ] ];
		}

		if (data.hasOwnProperty('message') && data.message.length > 0) {
			return data.message;
		}

		if (response.data === 404) {
			return 'Not Found';
		}

		if (typeof data === 'string') {
			return data;
		}

		return 'Sorry! An unknown error has occurred';
	}

	/**
	 * Return form errors if they exist, or fallback to getErrorMessage if no form errors can be found
	 *
	 * @param error
	 * @return {Object}
	 */
	getFormErrors(error) {
		if (error.response && error.response.data && error.response.data.errors) {
			return error.response.data.errors;
		}

		return { error: this.getErrorMessage(error) };
	}

}

export default {

	beforeCreate(context, inject) {
		const api = new Proxy(new API(context), {

			get(api, field) {
				if (field in api) return api[ field ];

				const instance = api.instance();

				if (field in instance) return instance[ field ];
			},

		});

		// Inject api everywhere
		inject('api', api);
	},

	created({ app }) {
		global.app = app;
	},

};
